import { type Blueprint, type Kitchen, NoteType } from '@rta/rta-blueprint-library';

import { formatBlueprintDate } from './format-blueprint-date';
import { getBlueprintCreationDate, INITIAL_DATE_OVERRIDE_KEY } from './get-blueprint-creation-date';
import { overrideToPlainText } from '../shared/sanitize-override-html';

/** Rows the revision log always draws, before any the user adds with + Add Row. */
export const REVISION_ROW_COUNT = 6;

export type StampInitialBlueprintDateParams = {
	kitchen: Kitchen | null;
	blueprint: Blueprint | null;
};

export type StampRevisionRowsParams = {
	kitchen: Kitchen | null;
	currentUserInitials?: string;
	/** Export only — generating the PDF/PPTX is the act that makes a date a fact, so nothing else commits one. */
	commitDates?: boolean;
	now?: Date;
};

/**
 * Freezes the BP's creation date the first time the sheet is exported to PDF or PPTX, and never again:
 * the issued document is what makes the date a fact, so that is the moment it stops being recomputed.
 * Merely opening the blueprint view displays the derived date without writing anything.
 */
export const stampInitialBlueprintDate = (params: StampInitialBlueprintDateParams): void => {
	const { kitchen, blueprint } = params;
	if (!kitchen || kitchen.blueprintOverrides?.[INITIAL_DATE_OVERRIDE_KEY] !== undefined) return;

	const creationDate = getBlueprintCreationDate({ kitchen, blueprint });
	if (creationDate) kitchen.setBlueprintOverride(INITIAL_DATE_OVERRIDE_KEY, creationDate);
};

/**
 * Stamps the editing user's initials onto every filled-in revision row, and — on export only — today's
 * date onto any such row that has never been dated. A row counts as filled in once its Rev cell holds
 * something, so an untouched row stays blank instead of carrying a date for a revision nobody made.
 * Initials land as the row is typed, keeping blame with the author; the date waits for the export,
 * because a revision's date means the day its document was issued.
 */
export const stampRevisionRows = (params: StampRevisionRowsParams): void => {
	const { kitchen, currentUserInitials = '', commitDates = false, now = new Date() } = params;
	if (!kitchen) return;

	const overrides = kitchen.blueprintOverrides ?? {};
	const revisionNoteCount = kitchen.notes?.children.filter((note) => note.type === NoteType.Revision).length ?? 0;
	const extraRows = parseInt(overrides['revision.extraRows'] ?? '', 10) || 0;
	const stampedDate = formatBlueprintDate(now);

	for (let index = 1 + revisionNoteCount; index < REVISION_ROW_COUNT + extraRows; index++) {
		// Plain text, not the raw value: a formatting-only edit leaves markup with no visible content, which must still count as an empty cell.
		if (!overrideToPlainText(overrides[`revision.${index}.rev`] ?? '').trim()) continue;
		if (currentUserInitials && overrides[`revision.${index}.engineer`] === undefined) {
			kitchen.setBlueprintOverride(`revision.${index}.engineer`, currentUserInitials);
		}
		if (commitDates && overrides[`revision.${index}.date`] === undefined) {
			kitchen.setBlueprintOverride(`revision.${index}.date`, stampedDate);
		}
	}
};
